export type ThediracSightCategory =
  | 'grot-ondergronds'
  | 'prehistorie-archeologie'
  | 'kasteel-ruine'
  | 'historisch-dorp'
  | 'natuur-bos'
  | 'uitzicht-hoogte'
  | 'water-landschap'

export type ThediracSightMarkerType =
  | 'ingang'
  | 'bezoekerspunt'
  | 'plaatscentrum'
  | 'toegangsroute'
  | 'uitzichtpunt'
  | 'object'

export type ThediracSight = {
  id: string
  category: ThediracSightCategory
  name: string
  lon: number
  lat: number
  driveMinutes: number
  driveKm: number
  markerType: ThediracSightMarkerType
  descriptionNl: string
  descriptionFr: string
  visitNl: string
  visitFr: string
  whyWorthItNl?: string
  whyWorthItFr?: string
  source: string
  sourceUrl: string
}

// Publieke object-, ingang-, bezoekers- of plaatscoördinaten. De rijtijden zijn
// route-indicaties vanaf het centrum van Thédirac, berekend zonder verkeersdata.
// De selectie is betekenisgestuurd: dichtbij waar mogelijk, tot circa drie uur
// voor uitzonderlijke bestemmingen. Enkele unieke prehistorische locaties liggen
// net boven drie uur en worden in hun popup eerlijk als uitzondering benoemd.
export const THEDIRAC_SIGHTS: ThediracSight[] = [
  {
    id: 'grottes-de-cougnac',
    category: 'grot-ondergronds',
    name: 'Grottes de Cougnac',
    lon: 1.3736035,
    lat: 44.7585562,
    driveMinutes: 34,
    driveKm: 26.3,
    markerType: 'ingang',
    descriptionNl: 'Twee grotten met rijke druipsteenformaties en authentieke prehistorische kunst van ongeveer 30.000 jaar oud.',
    descriptionFr: 'Deux grottes aux riches concrétions et à l’art préhistorique authentique vieux d’environ 30 000 ans.',
    visitNl: 'Rondleiding; controleer openingstijden en reserveer in het seizoen.',
    visitFr: 'Visite guidée ; vérifier les horaires et réserver en saison.',
    source: 'Grottes de Cougnac — officiële bezoekersinformatie',
    sourceUrl: 'https://www.grottesdecougnac.com/'
  },
  {
    id: 'grotte-de-domme',
    category: 'grot-ondergronds',
    name: 'Grotte de Domme',
    lon: 1.2141686,
    lat: 44.8029236,
    driveMinutes: 36,
    driveKm: 31.6,
    markerType: 'ingang',
    descriptionNl: 'Ongeveer 450 meter aan druipsteengalerijen onder de bastide; de uitgang biedt uitzicht over de Dordognevallei.',
    descriptionFr: 'Environ 450 mètres de galeries concrétionnées sous la bastide ; la sortie ouvre sur la vallée de la Dordogne.',
    visitNl: 'Bezoek met gids; combineerbaar met het historische centrum van Domme.',
    visitFr: 'Visite guidée ; à combiner avec le centre historique de Domme.',
    source: 'Sites de Domme — officiële bezoekersinformatie',
    sourceUrl: 'https://sites-domme.com/la-grotte-de-domme/'
  },
  {
    id: 'grottes-de-lacave',
    category: 'grot-ondergronds',
    name: 'Grottes de Lacave',
    lon: 1.5579101,
    lat: 44.8458767,
    driveMinutes: 55,
    driveKm: 49.4,
    markerType: 'ingang',
    descriptionNl: 'Ondergronds treintje en circa 1,5 kilometer zalen en galerijen met uiteenlopende druipsteenformaties.',
    descriptionFr: 'Petit train souterrain puis environ 1,5 kilomètre de salles et galeries aux concrétions variées.',
    visitNl: 'Betaalde rondleiding van ongeveer 1 uur 20; vooraf opening controleren.',
    visitFr: 'Visite guidée payante d’environ 1 h 20 ; vérifier l’ouverture.',
    source: 'Grottes de Lacave — officiële bezoekersinformatie',
    sourceUrl: 'https://www.les-grottes-de-lacave.com/'
  },
  {
    id: 'grotte-des-carbonnieres',
    category: 'grot-ondergronds',
    name: 'Grotte des Carbonnières',
    lon: 1.5667871,
    lat: 44.830555,
    driveMinutes: 56,
    driveKm: 51.7,
    markerType: 'ingang',
    descriptionNl: 'Een traject van ongeveer 350 meter door zes zalen, waaronder een opvallend grote zaal met concreties.',
    descriptionFr: 'Un parcours d’environ 350 mètres dans six salles, dont une vaste salle particulièrement riche en concrétions.',
    visitNl: 'Betaalde grot; controleer seizoensopening en vertrektijden.',
    visitFr: 'Grotte payante ; vérifier l’ouverture saisonnière et les départs.',
    source: 'Grotte des Carbonnières — officiële bezoekersinformatie',
    sourceUrl: 'https://www.grottedescarbonnieres.com/'
  },
  {
    id: 'grotte-des-merveilles',
    category: 'grot-ondergronds',
    name: 'Grotte des Merveilles',
    lon: 1.6253677,
    lat: 44.8051162,
    driveMinutes: 58,
    driveKm: 53.7,
    markerType: 'ingang',
    descriptionNl: 'Compacte grot bij Rocamadour met kristallisaties en ongeveer zeventig prehistorische dier- en handmotieven.',
    descriptionFr: 'Petite grotte près de Rocamadour, avec cristallisations et environ soixante-dix motifs préhistoriques.',
    visitNl: 'Rondleiding; vooraf opening en reservering controleren.',
    visitFr: 'Visite guidée ; vérifier à l’avance ouverture et réservation.',
    source: 'Grotte des Merveilles — officiële bezoekersinformatie',
    sourceUrl: 'https://grottedesmerveilles.com/'
  },
  {
    id: 'grotte-du-pech-merle',
    category: 'grot-ondergronds',
    name: 'Grotte du Pech Merle',
    lon: 1.6443372,
    lat: 44.507507,
    driveMinutes: 62,
    driveKm: 39.3,
    markerType: 'ingang',
    descriptionNl: 'Originele beschilderde grot met meer dan 20.000 jaar oude paarden, mammoeten, handafdrukken en menselijke sporen.',
    descriptionFr: 'Grotte ornée authentique avec chevaux, mammouths, mains et traces humaines vieux de plus de 20 000 ans.',
    visitNl: 'Bezoekersaantal is beperkt; ruim vooraf reserveren is verstandig.',
    visitFr: 'Le nombre de visiteurs est limité ; réserver bien à l’avance.',
    source: 'Pech Merle — officiële bezoekersinformatie',
    sourceUrl: 'https://www.pechmerle.com/'
  },
  {
    id: 'gouffre-de-padirac',
    category: 'grot-ondergronds',
    name: 'Gouffre de Padirac',
    lon: 1.7503915,
    lat: 44.8581995,
    driveMinutes: 68,
    driveKm: 58.5,
    markerType: 'ingang',
    descriptionNl: 'Monumentale karstschacht met een ondergrondse rivier; een deel van het bezoek gaat per boot.',
    descriptionFr: 'Gouffre karstique monumental traversé par une rivière souterraine ; une partie de la visite se fait en barque.',
    visitNl: 'Beroemd randgeval op circa 1 uur 08; reserveren en veel trappen verwachten.',
    visitFr: 'Site majeur en bord de zone, à environ 1 h 08 ; réserver et prévoir de nombreux escaliers.',
    source: 'Gouffre de Padirac — officiële bezoekersinformatie',
    sourceUrl: 'https://www.gouffre-de-padirac.com/'
  },
  {
    id: 'phosphatieres-cloup-aural',
    category: 'grot-ondergronds',
    name: 'Phosphatières du Cloup d’Aural',
    lon: 1.690853,
    lat: 44.3511007,
    driveMinutes: 63,
    driveKm: 56.7,
    markerType: 'ingang',
    descriptionNl: 'Voormalige fosfaatmijn in een open karstschacht, met fossielen uit een tijdreeks van ongeveer dertig miljoen jaar.',
    descriptionFr: 'Ancienne mine de phosphate dans un gouffre à ciel ouvert, avec des fossiles couvrant environ trente millions d’années.',
    visitNl: 'Geologisch bezoekersgebied; stevige schoenen en opening vooraf controleren.',
    visitFr: 'Site géologique aménagé ; bonnes chaussures et ouverture à vérifier.',
    source: 'Phosphatières du Cloup d’Aural — officiële informatie',
    sourceUrl: 'https://phosphatieres.com/'
  },
  {
    id: 'chateau-de-lantis',
    category: 'kasteel-ruine',
    name: 'Château de Lantis',
    lon: 1.3191686,
    lat: 44.6757735,
    driveMinutes: 14,
    driveKm: 10.9,
    markerType: 'object',
    descriptionNl: 'Versterkt huis, bekend sinds de 15e eeuw en na langdurig verval gerestaureerd; binnen ligt een zeldzame stervormige vloer.',
    descriptionFr: 'Maison forte connue depuis le XVe siècle, restaurée après un long abandon ; elle conserve un rare dallage en étoile.',
    visitNl: 'Particulier monument; alleen bezoeken volgens de gepubliceerde openingsmomenten.',
    visitFr: 'Monument privé ; visite uniquement selon les ouvertures publiées.',
    source: 'Château de Lantis — officiële informatie',
    sourceUrl: 'https://www.chateau-de-lantis.fr/'
  },
  {
    id: 'chateau-de-bonaguil',
    category: 'kasteel-ruine',
    name: 'Château de Bonaguil',
    lon: 1.0142418,
    lat: 44.5382678,
    driveMinutes: 44,
    driveKm: 36.6,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Enorme laatmiddeleeuwse rotsvesting met torens, grachten, kazematten en vrijwel het volledige verdedigingssysteem.',
    descriptionFr: 'Immense forteresse de la fin du Moyen Âge, avec tours, fossés, casemates et système défensif presque complet.',
    visitNl: 'Betaald monument; reken op trappen en ongelijke ondergrond.',
    visitFr: 'Monument payant ; prévoir escaliers et sols irréguliers.',
    source: 'Château de Bonaguil — officiële informatie',
    sourceUrl: 'https://www.chateau-bonaguil.com/'
  },
  {
    id: 'chateau-de-castelnaud',
    category: 'kasteel-ruine',
    name: 'Château de Castelnaud',
    lon: 1.1480437,
    lat: 44.8160173,
    driveMinutes: 43,
    driveKm: 37.3,
    markerType: 'bezoekerspunt',
    descriptionNl: '13e-eeuwse vesting boven de Dordogne met een museum voor middeleeuwse oorlogsvoering en weids uitzicht.',
    descriptionFr: 'Forteresse du XIIIe siècle dominant la Dordogne, avec musée de la guerre médiévale et vaste panorama.',
    visitNl: 'Betaald; steile straat en veel trappen, beperkt geschikt bij mobiliteitsproblemen.',
    visitFr: 'Payant ; rue raide et nombreux escaliers, accessibilité limitée.',
    source: 'Château de Castelnaud — officiële informatie',
    sourceUrl: 'https://castelnaud.com/'
  },
  {
    id: 'chateau-de-beynac',
    category: 'kasteel-ruine',
    name: 'Château de Beynac',
    lon: 1.1455347,
    lat: 44.8401415,
    driveMinutes: 47,
    driveKm: 38.9,
    markerType: 'bezoekerspunt',
    descriptionNl: '12e-eeuwse klifvesting met een geschiedenis rond Richard Leeuwenhart en uitzicht over de Dordogne.',
    descriptionFr: 'Forteresse du XIIe siècle sur falaise, liée à Richard Cœur de Lion, dominant la Dordogne.',
    visitNl: 'Betaald; steile klim, kasseien en trappen.',
    visitFr: 'Payant ; montée raide, pavés et escaliers.',
    source: 'Château de Beynac — officiële informatie',
    sourceUrl: 'https://chateau-beynac.com/'
  },
  {
    id: 'chateau-de-fenelon',
    category: 'kasteel-ruine',
    name: 'Château de Fénelon',
    lon: 1.3502674,
    lat: 44.8423207,
    driveMinutes: 49,
    driveKm: 35.3,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Hooggelegen, gemeubileerde vesting waarin middeleeuwse verdedigingsbouw en later wooncomfort samenkomen.',
    descriptionFr: 'Forteresse perchée et meublée, réunissant architecture défensive médiévale et aménagements résidentiels ultérieurs.',
    visitNl: 'Particulier en seizoensgebonden; opening vooraf controleren.',
    visitFr: 'Site privé et saisonnier ; vérifier l’ouverture.',
    source: 'Château de Fénelon — officiële informatie',
    sourceUrl: 'https://chateau-fenelon.fr/'
  },
  {
    id: 'chateau-de-commarque',
    category: 'kasteel-ruine',
    name: 'Château de Commarque',
    lon: 1.1017648,
    lat: 44.9417772,
    driveMinutes: 63,
    driveKm: 54.7,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Herontdekte burchtruïne in de beboste Beunevallei, boven holwoningen en archeologische lagen uit de prehistorie.',
    descriptionFr: 'Forteresse ruinée redécouverte dans la vallée boisée de la Beune, au-dessus d’habitats troglodytiques et de niveaux préhistoriques.',
    visitNl: 'Betaald; circa 600 meter lopen vanaf de hoofdparking, aparte PMR-parking beschikbaar.',
    visitFr: 'Payant ; environ 600 m depuis le parking principal, parking PMR dédié.',
    source: 'Château de Commarque — officiële informatie',
    sourceUrl: 'https://commarque.com/'
  },
  {
    id: 'chateau-des-anglais-bouzies',
    category: 'kasteel-ruine',
    name: 'Château des Anglais de Bouziès',
    lon: 1.6425039,
    lat: 44.486515,
    driveMinutes: 59,
    driveKm: 50.2,
    markerType: 'object',
    descriptionNl: 'Resten van een 11e- of 12e-eeuwse versterkte schuilplaats, hoog tegen de rotswand boven de Lot.',
    descriptionFr: 'Vestiges d’un refuge fortifié des XIe-XIIe siècles, accroché à la falaise au-dessus du Lot.',
    visitNl: 'Interieur niet toegankelijk; goed zichtbaar vanaf de weg en metalen brug.',
    visitFr: 'Intérieur inaccessible ; visible depuis la route et le pont métallique.',
    source: 'Lot Tourisme — Château des Anglais de Bouziès',
    sourceUrl: 'https://www.tourisme-lot.com/offres/chateau-des-anglais-de-bouzies-bouzies-fr-3849872/'
  },
  {
    id: 'chateau-de-biron',
    category: 'kasteel-ruine',
    name: 'Château de Biron',
    lon: 0.8721922,
    lat: 44.6320819,
    driveMinutes: 62,
    driveKm: 54.2,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Groot kasteelcomplex met bijna duizend jaar bouwgeschiedenis, van middeleeuwse vesting tot renaissance- en classicistische vleugels.',
    descriptionFr: 'Grand ensemble couvrant près de mille ans d’architecture, de la forteresse médiévale aux ailes Renaissance et classiques.',
    visitNl: 'Betaald monument aan de westelijke rand van het gebied; opening controleren.',
    visitFr: 'Monument payant en bordure ouest de la zone ; vérifier l’ouverture.',
    source: 'Château de Biron — officiële informatie',
    sourceUrl: 'https://chateau-biron.fr/'
  },
  {
    id: 'degagnazes',
    category: 'historisch-dorp',
    name: 'Dégagnazès',
    lon: 1.3599109,
    lat: 44.6107669,
    driveMinutes: 9,
    driveKm: 4.9,
    markerType: 'plaatscentrum',
    descriptionNl: 'Afgelegen historisch hameau in het bos met resten en herinneringen aan een priorij; oud, stil, maar niet verlaten.',
    descriptionFr: 'Hameau historique isolé dans les bois, marqué par l’ancienne présence d’un prieuré ; ancien et paisible, mais non abandonné.',
    visitNl: 'Vrij toegankelijk; officiële eenvoudige rondwandeling van circa 3,7 kilometer.',
    visitFr: 'Accès libre ; boucle officielle facile d’environ 3,7 km.',
    source: 'Lot Tourisme — Circuit du Dégagnazès',
    sourceUrl: 'https://www.tourisme-lot.com/offres/circuit-du-degagnazes-peyrilles-fr-658277/'
  },
  {
    id: 'gourdon-medieval',
    category: 'historisch-dorp',
    name: 'Middeleeuws Gourdon',
    lon: 1.3824611,
    lat: 44.7377882,
    driveMinutes: 27,
    driveKm: 23.7,
    markerType: 'uitzichtpunt',
    descriptionNl: 'Middeleeuwse heuvelstad; de esplanade van het verdwenen kasteel biedt een oriëntatiepunt en uitzicht over de Bouriane.',
    descriptionFr: 'Ville médiévale en hauteur ; l’esplanade de l’ancien château offre un repère et un panorama sur la Bouriane.',
    visitNl: 'Vrij stadsbezoek; steile straten richting de top.',
    visitFr: 'Visite urbaine libre ; rues raides vers le sommet.',
    source: 'Lot Tourisme — Gourdon',
    sourceUrl: 'https://www.tourisme-lot.com/gourdon/visiter-gourdon/sites-a-visiter-gourdon/'
  },
  {
    id: 'la-roque-gageac',
    category: 'historisch-dorp',
    name: 'La Roque-Gageac',
    lon: 1.1834985,
    lat: 44.8255441,
    driveMinutes: 36,
    driveKm: 32.6,
    markerType: 'plaatscentrum',
    descriptionNl: 'Beschermd dorp tussen een hoge rotswand en de Dordogne, met huizen tegen de klif en een troglodietenfort.',
    descriptionFr: 'Village classé serré entre une haute falaise et la Dordogne, avec maisons adossées au rocher et fort troglodytique.',
    visitNl: 'Vrij te wandelen; in drukke perioden buiten het centrum parkeren.',
    visitFr: 'Promenade libre ; en période chargée, stationner hors du centre.',
    source: 'Gemeente La Roque-Gageac — officiële informatie',
    sourceUrl: 'https://laroquegageac.fr/'
  },
  {
    id: 'sarlat-la-caneda',
    category: 'historisch-dorp',
    name: 'Sarlat-la-Canéda',
    lon: 1.2161321,
    lat: 44.8879431,
    driveMinutes: 46,
    driveKm: 40.3,
    markerType: 'plaatscentrum',
    descriptionNl: 'Uitzonderlijk dicht historisch centrum met middeleeuwse straten, renaissancehuizen, pleinen en markthallen.',
    descriptionFr: 'Centre historique remarquablement dense, avec rues médiévales, hôtels Renaissance, places et halles.',
    visitNl: 'Vrij stadsbezoek; kasseien en vaak grote drukte op marktdagen.',
    visitFr: 'Visite libre ; pavés et forte affluence les jours de marché.',
    source: 'Sarlat Tourisme — officiële informatie',
    sourceUrl: 'https://www.sarlat-tourisme.com/'
  },
  {
    id: 'monpazier',
    category: 'historisch-dorp',
    name: 'Monpazier',
    lon: 0.89374,
    lat: 44.67998,
    driveMinutes: 53,
    driveKm: 47.5,
    markerType: 'plaatscentrum',
    descriptionNl: 'Bijna schoolboekachtige 13e-eeuwse bastide met regelmatig stratenplan, centraal plein en historische markthal.',
    descriptionFr: 'Bastide du XIIIe siècle presque exemplaire, avec plan régulier, place centrale et halle historique.',
    visitNl: 'Vrij toegankelijk centrum; parkeren aan de rand is het eenvoudigst.',
    visitFr: 'Centre librement accessible ; stationnement plus simple en périphérie.',
    source: 'Pays de Bergerac Tourisme — Monpazier',
    sourceUrl: 'https://www.pays-bergerac-tourisme.com/en/discover/old-stones/bastide-towns/monpazier'
  },
  {
    id: 'rocamadour',
    category: 'historisch-dorp',
    name: 'Rocamadour',
    lon: 1.6181714,
    lat: 44.7995467,
    driveMinutes: 57,
    driveKm: 49,
    markerType: 'plaatscentrum',
    descriptionNl: 'Verticale pelgrimsstad in de wand van de Alzouvallei, met heiligdommen, poorten en huizen boven elkaar.',
    descriptionFr: 'Cité de pèlerinage verticale dans la falaise de l’Alzou, avec sanctuaires, portes et maisons superposées.',
    visitNl: 'Veel hoogteverschil en trappen; liften kunnen een deel van de klim vervangen.',
    visitFr: 'Fort dénivelé et nombreux escaliers ; des ascenseurs évitent une partie de la montée.',
    source: 'Vallée de la Dordogne Tourisme — officiële informatie',
    sourceUrl: 'https://www.vallee-dordogne.com/'
  },
  {
    id: 'saint-cirq-lapopie',
    category: 'historisch-dorp',
    name: 'Saint-Cirq-Lapopie',
    lon: 1.6687454,
    lat: 44.4647712,
    driveMinutes: 67,
    driveKm: 56.8,
    markerType: 'plaatscentrum',
    descriptionNl: 'Spectaculair middeleeuws dorp op een klif boven de Lot, met smalle straten, vakwerkhuizen en ruïnes op de top.',
    descriptionFr: 'Village médiéval spectaculaire sur une falaise dominant le Lot, avec ruelles, maisons à colombages et ruines sommitales.',
    visitNl: 'Beroemd randgeval op circa 1 uur 07; parkeerterreinen liggen buiten het oude dorp.',
    visitFr: 'Site majeur en bord de zone, à environ 1 h 07 ; parkings hors du vieux village.',
    source: 'Lot Tourisme — Saint-Cirq-Lapopie',
    sourceUrl: 'https://www.tourisme-lot.com/saint-cirq-lapopie/'
  },
  {
    id: 'hameau-barrieres',
    category: 'historisch-dorp',
    name: 'Verlaten hameau Barrières',
    lon: 1.6918727,
    lat: 44.8729366,
    driveMinutes: 71,
    driveKm: 60.4,
    markerType: 'plaatscentrum',
    descriptionNl: 'Geleidelijk verlaten gehucht met kalkstenen huisresten, broodoven en agrarische sporen; een echte ruïneplek, geen verzonnen spookdorp.',
    descriptionFr: 'Hameau progressivement abandonné, avec ruines de maisons calcaires, four à pain et traces agricoles.',
    visitNl: 'Randgeval op circa 1 uur 11; vrij te bewandelen, maar niet op of in instabiele muren klimmen.',
    visitFr: 'Site en bord de zone, à environ 1 h 11 ; accès pédestre libre, sans grimper sur les murs instables.',
    source: 'Association DECLAM — chantier du hameau de Barrières',
    sourceUrl: 'https://declam.jimdofree.com/nos-chantiers/miers/'
  },
  {
    id: 'landes-frau-degagnazes',
    category: 'natuur-bos',
    name: 'Landes du Frau-Dégagnazès',
    lon: 1.34896874,
    lat: 44.6313445,
    driveMinutes: 10,
    driveKm: 7.9,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Zeldzaam zuur eiland in het kalklandschap met heide, gaspeldoorn, veenachtige zones en bos.',
    descriptionFr: 'Rare îlot acide dans le paysage calcaire, mêlant landes, ajoncs, zones tourbeuses et bois.',
    visitNl: 'Vrij toegankelijk; officiële zeer eenvoudige lus van circa 2,6 kilometer.',
    visitFr: 'Accès libre ; boucle officielle très facile d’environ 2,6 km.',
    source: 'Lot Tourisme — Circuit des Landes du Frau',
    sourceUrl: 'https://www.tourisme-lot.com/offres/circuit-des-landes-du-frau-lavercantiere-fr-659657/'
  },
  {
    id: 'vallee-masse-marais-arques',
    category: 'natuur-bos',
    name: 'Vallée de la Masse · Marais des Arques',
    lon: 1.25070333,
    lat: 44.6016823,
    driveMinutes: 12,
    driveKm: 7.5,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Waterrijk natuurgebied met moeras, vlonderpad, soortenrijkdom en resten van oude ijzerbewerking.',
    descriptionFr: 'Espace humide avec marais, caillebotis, forte biodiversité et traces d’ancienne métallurgie du fer.',
    visitNl: 'Vrij toegankelijk; officiële gemakkelijke lus van circa 4,1 kilometer.',
    visitFr: 'Accès libre ; boucle officielle facile d’environ 4,1 km.',
    source: 'Lot Tourisme — Circuit du Marais des Arques',
    sourceUrl: 'https://www.tourisme-lot.com/offres/circuit-du-marais-des-arques-les-arques-fr-659328/'
  },
  {
    id: 'marais-de-grolejac',
    category: 'natuur-bos',
    name: 'Réserve naturelle du Marais de Groléjac',
    lon: 1.2967756,
    lat: 44.8069148,
    driveMinutes: 38,
    driveKm: 31.7,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Regionaal natuurreservaat rond het grootste kalkmoeras van de Dordogne, met vlonderpad en observatiepunten.',
    descriptionFr: 'Réserve naturelle régionale protégeant le plus grand marais calcaire de Dordogne, avec caillebotis et observatoires.',
    visitNl: 'Vrij toegankelijk vanaf de bezoekerszijde; blijf op de aangelegde route.',
    visitFr: 'Accès libre côté visiteurs ; rester sur le parcours aménagé.',
    source: 'Dordogne Périgord Tourisme — Réserve du Marais de Groléjac',
    sourceUrl: 'https://www.dordogne-perigord-tourisme.fr/offres/reserve-naturelle-regionale-du-marais-de-grolejac-grolejac-grolejac-fr-2940476/'
  },
  {
    id: 'foret-de-la-braunhie',
    category: 'natuur-bos',
    name: 'Forêt de la Braunhie · Planagrèze',
    lon: 1.66251897,
    lat: 44.6357193,
    driveMinutes: 46,
    driveKm: 35.7,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Uitgestrekt oud eiken- en karstbos met droog grasland, igues, Saint-Namphaise-poelen en droogstenen erfgoed.',
    descriptionFr: 'Vaste forêt ancienne de chênes et de karst, avec pelouses sèches, igues, lacs de Saint-Namphaise et pierre sèche.',
    visitNl: 'Vrij toegankelijk; officiële eenvoudige Planagrèze-lus van circa 6,3 kilometer.',
    visitFr: 'Accès libre ; boucle officielle facile de Planagrèze, environ 6,3 km.',
    source: 'Lot Tourisme — Circuit ENS de Planagrèze',
    sourceUrl: 'https://www.tourisme-lot.com/offres/circuit-ens-de-planagreze-caniac-du-causse-fr-659260/'
  },
  {
    id: 'moulin-du-saut',
    category: 'natuur-bos',
    name: 'Moulin du Saut · Gorges de l’Alzou',
    lon: 1.67340041,
    lat: 44.7887322,
    driveMinutes: 57,
    driveKm: 48.5,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Ruïnes van een watermolen in de wilde, beboste kloof van de Alzou; natuur en industrieel verleden op één plek.',
    descriptionFr: 'Ruines d’un moulin dans les gorges sauvages et boisées de l’Alzou ; nature et passé industriel réunis.',
    visitNl: 'Vrije wandelroute van circa 4,6 kilometer; onverhard en plaatselijk rotsachtig.',
    visitFr: 'Boucle libre d’environ 4,6 km ; sentier non revêtu et parfois rocheux.',
    source: 'Lot Tourisme — Circuit du Moulin du Saut',
    sourceUrl: 'https://www.tourisme-lot.com/offres/circuit-du-moulin-du-saut-gramat-fr-659458/'
  },
  {
    id: 'mont-saint-cyr',
    category: 'uitzicht-hoogte',
    name: 'Mont Saint-Cyr',
    lon: 1.4543746,
    lat: 44.4425136,
    driveMinutes: 44,
    driveKm: 29.9,
    markerType: 'uitzichtpunt',
    descriptionNl: 'Hoog uitzichtpunt boven Cahors met een breed panorama over de stad en de lus van de Lot.',
    descriptionFr: 'Belvédère dominant Cahors, avec un large panorama sur la ville et la boucle du Lot.',
    visitNl: 'Open uitzichtpunt; weg en parking dicht bij de top.',
    visitFr: 'Belvédère en accès libre ; route et stationnement près du sommet.',
    source: 'OpenStreetMap — openbaar uitzichtpunt',
    sourceUrl: 'https://www.openstreetmap.org/node/12033327348'
  },
  {
    id: 'cingle-de-montfort',
    category: 'uitzicht-hoogte',
    name: 'Cingle de Montfort',
    lon: 1.2590671,
    lat: 44.843408,
    driveMinutes: 41,
    driveKm: 36.8,
    markerType: 'uitzichtpunt',
    descriptionNl: 'Uitzicht over een uitgesproken meander van de Dordogne en het landschap rond Château de Montfort.',
    descriptionFr: 'Point de vue sur un méandre marqué de la Dordogne et le paysage autour du château de Montfort.',
    visitNl: 'Klein openbaar weguitzicht; veilig parkeren en verkeer in de gaten houden.',
    visitFr: 'Petit point de vue routier public ; stationner prudemment et surveiller la circulation.',
    source: 'OpenStreetMap — openbaar uitzichtpunt',
    sourceUrl: 'https://www.openstreetmap.org/node/2619895785'
  },
  {
    id: 'jardins-marqueyssac',
    category: 'uitzicht-hoogte',
    name: 'Jardins de Marqueyssac · Belvédère',
    lon: 1.1707802,
    lat: 44.8264764,
    driveMinutes: 44,
    driveKm: 36.8,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Historische tuinen op een rotskam, met gesnoeide buxus, wandelpaden en een beroemd panorama over de Dordogne.',
    descriptionFr: 'Jardins historiques sur un éperon, avec buis taillés, promenades et célèbre panorama sur la Dordogne.',
    visitNl: 'Betaald en seizoensgebonden; meerdere wandelroutes over het terrein.',
    visitFr: 'Payant et saisonnier ; plusieurs itinéraires pédestres dans le domaine.',
    source: 'Jardins de Marqueyssac — officiële informatie',
    sourceUrl: 'https://www.marqueyssac.com/'
  },
  {
    id: 'lac-vert-catus',
    category: 'water-landschap',
    name: 'Lac Vert · Catus',
    lon: 1.3260532,
    lat: 44.5488504,
    driveMinutes: 11,
    driveKm: 9.2,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Groen recreatiemeer in het dal van de Vert, geschikt als korte landschapswandeling dicht bij Thédirac.',
    descriptionFr: 'Lac de loisirs dans la vallée du Vert, adapté à une courte promenade paysagère près de Thédirac.',
    visitNl: 'Vrij wandelen; zwem- en horecavoorzieningen zijn seizoensgebonden en moeten ter plaatse worden gecontroleerd.',
    visitFr: 'Promenade libre ; baignade et services sont saisonniers et à vérifier sur place.',
    source: 'OpenStreetMap — openbaar meerobject',
    sourceUrl: 'https://www.openstreetmap.org/way/110451498'
  },
  {
    id: 'moulin-fortifie-cougnaguet',
    category: 'water-landschap',
    name: 'Moulin fortifié de Cougnaguet',
    lon: 1.5605613,
    lat: 44.8118899,
    driveMinutes: 52,
    driveKm: 46.9,
    markerType: 'object',
    descriptionNl: 'Versterkte 13e- en 14e-eeuwse watermolen aan de Ouysse, met behouden maalwerk en een bijzonder rivierlandschap.',
    descriptionFr: 'Moulin à eau fortifié des XIIIe-XIVe siècles sur l’Ouysse, avec mécanisme conservé et remarquable paysage de rivière.',
    visitNl: 'Landelijke bezoekersplek; opening vooraf controleren.',
    visitFr: 'Site rural visitable ; vérifier l’ouverture à l’avance.',
    source: 'Moulin de Cougnaguet — officiële informatie',
    sourceUrl: 'https://www.cougnaguet.com/'
  },
  {
    id: 'chemin-halage-ganil',
    category: 'water-landschap',
    name: 'Chemin de halage de Ganil',
    lon: 1.6459054,
    lat: 44.4814726,
    driveMinutes: 61,
    driveKm: 51.1,
    markerType: 'object',
    descriptionNl: 'Jaagpad langs de Lot dat bij Ganil in de klif is uitgehakt, met een hedendaags bas-reliëf in de rotswand.',
    descriptionFr: 'Chemin de halage le long du Lot, taillé dans la falaise à Ganil et orné d’un bas-relief contemporain.',
    visitNl: 'Ongeveer 3 kilometer heen en terug vanaf Bouziès; controleer waterstand en padconditie.',
    visitFr: 'Environ 3 km aller-retour depuis Bouziès ; vérifier niveau d’eau et état du chemin.',
    source: 'Lot Tourisme — Chemin de halage',
    sourceUrl: 'https://www.tourisme-lot.com/saint-cirq-lapopie/activites-saint-cirq-lapopie/chemin-de-halage/'
  },
  {
    id: 'jardins-eau-carsac',
    category: 'water-landschap',
    name: 'Les Jardins d’Eau',
    lon: 1.271763,
    lat: 44.8266317,
    driveMinutes: 44,
    driveKm: 35.3,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Watertuin en arboretum met lotussen, waterlelies, papyrus, bruggetjes en een speels waterlabyrint.',
    descriptionFr: 'Jardin aquatique et arboretum avec lotus, nénuphars, papyrus, passerelles et labyrinthe d’eau.',
    visitNl: 'Betaald en seizoensgebonden; bloei en opening vooraf controleren.',
    visitFr: 'Payant et saisonnier ; vérifier floraison et ouverture.',
    source: 'Les Jardins d’Eau — officiële informatie',
    sourceUrl: 'https://jardinsdeau.com/'
  },
  {
    id: 'pont-valentre',
    category: 'water-landschap',
    name: 'Pont Valentré',
    lon: 1.4316703,
    lat: 44.4450553,
    driveMinutes: 31,
    driveKm: 25.8,
    markerType: 'object',
    descriptionNl: 'Iconische versterkte middeleeuwse brug over de Lot met drie torens; onderdeel van het UNESCO-pelgrimserfgoed van Cahors.',
    descriptionFr: 'Pont médiéval fortifié emblématique à trois tours sur le Lot, inscrit dans le patrimoine UNESCO de Cahors.',
    visitNl: 'Buitenzijde en oversteek vrij toegankelijk; parkeren in Cahors.',
    visitFr: 'Extérieur et traversée en accès libre ; stationnement à Cahors.',
    source: 'Lot Tourisme — Pont Valentré',
    sourceUrl: 'https://www.tourisme-lot.com/cahors/visiter-cahors/le-pont-valentre/'
  },
  {
    id: 'lascaux-iv',
    category: 'prehistorie-archeologie',
    name: 'Lascaux IV',
    lon: 1.1695226,
    lat: 45.0578898,
    driveMinutes: 71,
    driveKm: 66.1,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Volledige replica van de beroemde beschilderde grot, gecombineerd met een uitgebreid centrum over de kunst en het onderzoek.',
    descriptionFr: 'Réplique complète de la célèbre grotte ornée, complétée par un vaste centre consacré à l’art et à la recherche.',
    visitNl: 'De originele grot is niet toegankelijk; dit is bewust de nauwkeurige replica Lascaux IV. Vooraf reserveren.',
    visitFr: 'La grotte originale est fermée ; il s’agit volontairement de la réplique fidèle Lascaux IV. Réserver.',
    whyWorthItNl: 'De beste complete uitleg van Lascaux zonder te doen alsof de replica het origineel is.',
    whyWorthItFr: 'La lecture la plus complète de Lascaux, sans prétendre que la réplique est l’original.',
    source: 'Lascaux IV — officiële bezoekersinformatie',
    sourceUrl: 'https://lascaux.fr/'
  },
  {
    id: 'grotte-de-font-de-gaume',
    category: 'grot-ondergronds',
    name: 'Grotte de Font-de-Gaume',
    lon: 1.0285602,
    lat: 44.9353238,
    driveMinutes: 64,
    driveKm: 60.1,
    markerType: 'ingang',
    descriptionNl: 'Een van de laatste Franse grotten waar nog authentieke polychrome paleolithische schilderingen te bezoeken zijn.',
    descriptionFr: 'L’une des dernières grottes françaises où l’on peut encore visiter d’authentiques peintures paléolithiques polychromes.',
    visitNl: 'Zeer weinig plaatsen; begeleid bezoek en ruim vooraf reserveren. Vanaf de ontvangst volgt een klim naar de grot.',
    visitFr: 'Places très limitées ; visite guidée et réservation très anticipée. Montée depuis l’accueil.',
    whyWorthItNl: 'Dit is echte prehistorische kunst op de oorspronkelijke wand, geen facsimile.',
    whyWorthItFr: 'Ici, l’art préhistorique est authentique et sur sa paroi d’origine, sans fac-similé.',
    source: 'Centre des monuments nationaux — Sites des Eyzies',
    sourceUrl: 'https://www.sites-les-eyzies.fr/'
  },
  {
    id: 'grotte-des-combarelles',
    category: 'grot-ondergronds',
    name: 'Grotte des Combarelles',
    lon: 1.0421366,
    lat: 44.9437112,
    driveMinutes: 62,
    driveKm: 58.2,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Smalle authentieke grot met honderden fijn gegraveerde dieren en menselijke figuren uit het Magdalénien.',
    descriptionFr: 'Étroite grotte authentique aux centaines de gravures fines d’animaux et de figures humaines du Magdalénien.',
    visitNl: 'Uitsluitend begeleid en met zeer beperkte capaciteit; reserveren. Het traject is smal en niet rolstoeltoegankelijk.',
    visitFr: 'Uniquement en visite guidée, capacité très réduite ; réserver. Parcours étroit, non accessible en fauteuil.',
    whyWorthItNl: 'Minder beroemd dan Lascaux, maar je staat wel voor het originele graveerwerk.',
    whyWorthItFr: 'Moins célèbre que Lascaux, mais les gravures observées sont bien les originales.',
    source: 'Centre des monuments nationaux — Sites des Eyzies',
    sourceUrl: 'https://www.sites-les-eyzies.fr/'
  },
  {
    id: 'grotte-de-rouffignac',
    category: 'grot-ondergronds',
    name: 'Grotte de Rouffignac',
    lon: 0.987912,
    lat: 45.0087737,
    driveMinutes: 85,
    driveKm: 73.6,
    markerType: 'ingang',
    descriptionNl: 'Uitgestrekt grottenlabyrint met meer dan 250 dierfiguren, vooral mammoeten, en sporen van holenberen.',
    descriptionFr: 'Vaste labyrinthe souterrain comptant plus de 250 figures animales, surtout des mammouths, et des traces d’ours des cavernes.',
    visitNl: 'Rondleiding per elektrisch treintje; weinig loopbelasting. Capaciteit is beperkt en de reserveringswijze wisselt per seizoen.',
    visitFr: 'Visite en petit train électrique, avec peu de marche. Capacité limitée ; modalités selon la saison.',
    whyWorthItNl: 'Een authentieke “mammoetgrot” die door het treintje ook op een slechte kniedag haalbaar is.',
    whyWorthItFr: 'Une authentique « grotte aux mammouths », accessible en train même un jour où les genoux protestent.',
    source: 'Grotte de Rouffignac — officiële informatie',
    sourceUrl: 'https://www.grottederouffignac.fr/index.php/fr/'
  },
  {
    id: 'musee-national-prehistoire',
    category: 'prehistorie-archeologie',
    name: 'Musée national de Préhistoire',
    lon: 1.0138378,
    lat: 44.9361063,
    driveMinutes: 65,
    driveKm: 60.9,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Nationaal referentiemuseum boven Les Eyzies met originele werktuigen, kunst, menselijke resten en regionale stratigrafie.',
    descriptionFr: 'Musée national de référence aux Eyzies, avec outils, art, restes humains et stratigraphies régionales originaux.',
    visitNl: 'Binnenmuseum met lift en voorzieningen; combineerbaar met Font-de-Gaume of een abri in de Vézèrevallei.',
    visitFr: 'Musée couvert avec ascenseur et services ; à combiner avec Font-de-Gaume ou un abri de la Vézère.',
    whyWorthItNl: 'Hier vallen losse grot- en vindplaatsbezoeken eindelijk in één chronologisch verhaal.',
    whyWorthItFr: 'Le lieu qui remet les grottes et gisements visités dans un seul récit chronologique.',
    source: 'Musée national de Préhistoire — officiële informatie',
    sourceUrl: 'https://musee-prehistoire-eyzies.fr/'
  },
  {
    id: 'abri-cro-magnon',
    category: 'prehistorie-archeologie',
    name: 'Abri Cro-Magnon',
    lon: 1.0096682,
    lat: 44.9405939,
    driveMinutes: 67,
    driveKm: 61.4,
    markerType: 'object',
    descriptionNl: 'De rotsschuilplaats waar in 1868 de beroemde vroege Homo-sapiensresten werden ontdekt die de naam Cro-Magnon kregen.',
    descriptionFr: 'L’abri où furent découverts en 1868 les célèbres restes d’Homo sapiens auxquels fut donné le nom Cro-Magnon.',
    visitNl: 'Historisch bronpunt. De Staat kocht het terrein in 2024; het is momenteel gesloten en een heropeningsdatum is nog niet bekend.',
    visitFr: 'Point historique. Racheté par l’État en 2024, le site est actuellement fermé sans date de réouverture connue.',
    whyWorthItNl: 'Niet open, wel te belangrijk om van het totaalbeeld te laten verdwijnen.',
    whyWorthItFr: 'Fermé, mais trop important pour disparaître de la carte d’ensemble.',
    source: 'Centre des monuments nationaux — Sites des Eyzies',
    sourceUrl: 'https://www.sites-les-eyzies.fr/decouvrir/l-abri-cro-magnon2/l-abri-cro-magnon'
  },
  {
    id: 'abri-du-cap-blanc',
    category: 'prehistorie-archeologie',
    name: 'Abri du Cap Blanc',
    lon: 1.0969444,
    lat: 44.9455556,
    driveMinutes: 66,
    driveKm: 60.8,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Rotsschuilplaats met een uitzonderlijke monumentale fries van dieren die in de kalkstenen wand is uitgehakt.',
    descriptionFr: 'Abri sous roche conservant une exceptionnelle frise monumentale d’animaux sculptée dans la paroi calcaire.',
    visitNl: 'Begeleid bezoek met beperkte capaciteit; reserveren. Controleer vooraf de toegankelijkheid en actuele ontvangstlocatie.',
    visitFr: 'Visite guidée à capacité limitée ; réserver. Vérifier l’accessibilité et le lieu d’accueil.',
    whyWorthItNl: 'Paleolithische sculptuur op ware grootte is veel zeldzamer dan geschilderde grotkunst.',
    whyWorthItFr: 'La sculpture paléolithique monumentale est bien plus rare que la peinture pariétale.',
    source: 'Centre des monuments nationaux — Sites des Eyzies',
    sourceUrl: 'https://www.sites-les-eyzies.fr/'
  },
  {
    id: 'village-de-la-madeleine',
    category: 'prehistorie-archeologie',
    name: 'Village de la Madeleine',
    lon: 1.0306882,
    lat: 44.968019,
    driveMinutes: 74,
    driveKm: 65.2,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Klif met een paleolithische typevindplaats en daarboven een bezoekbaar middeleeuws troglodietendorp aan de Vézère.',
    descriptionFr: 'Falaise réunissant le site éponyme du Magdalénien et, au-dessus, un village troglodytique médiéval visitable.',
    visitNl: 'Het prehistorische opgravingsterrein zelf is niet toegankelijk; het bovenliggende klifdorp wel. Reken op trappen en ongelijke paden.',
    visitFr: 'Le gisement préhistorique n’est pas accessible ; le village en falaise l’est. Prévoir marches et sols irréguliers.',
    whyWorthItNl: 'De plek gaf haar naam aan het Magdalénien en laat tegelijk eeuwen klifbewoning zien.',
    whyWorthItFr: 'Le lieu a donné son nom au Magdalénien et montre aussi des siècles d’habitat en falaise.',
    source: 'Village de la Madeleine — officiële informatie',
    sourceUrl: 'https://la-madeleine-perigord.com/'
  },
  {
    id: 'la-roque-saint-christophe',
    category: 'prehistorie-archeologie',
    name: 'La Roque Saint-Christophe',
    lon: 1.0687764,
    lat: 44.9890356,
    driveMinutes: 72,
    driveKm: 66.4,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Kilometerlange kalkklif met rotsschuilplaatsen en sporen van bewoning van de prehistorie tot een middeleeuwse vesting.',
    descriptionFr: 'Falaise calcaire longue d’un kilomètre, occupée de la Préhistoire jusqu’à la forteresse médiévale.',
    visitNl: 'Vrij of begeleid bezoek; ongeveer 80 treden retour. De grote terrassen maken veel van de site toch overzichtelijk.',
    visitFr: 'Visite libre ou guidée ; environ 80 marches aller-retour. De vastes terrasses structurent le parcours.',
    whyWorthItNl: 'Een zeldzame plek waar 55.000 jaar gebruik van dezelfde klif letterlijk boven elkaar ligt.',
    whyWorthItFr: 'Un rare lieu où 55 000 ans d’occupation d’une même falaise se lisent presque strate par strate.',
    source: 'La Roque Saint-Christophe — officiële informatie',
    sourceUrl: 'https://www.roque-st-christophe.com/'
  },
  {
    id: 'gouffre-de-proumeyssac',
    category: 'grot-ondergronds',
    name: 'Gouffre de Proumeyssac',
    lon: 0.9342052,
    lat: 44.890036,
    driveMinutes: 68,
    driveKm: 63,
    markerType: 'ingang',
    descriptionNl: 'Enorme ingerichte koepelgrot, de “Kristalkathedraal”, vol druipsteen, kristallisaties en versteende afzettingen.',
    descriptionFr: 'Immense cavité aménagée, la « Cathédrale de cristal », riche en concrétions et cristallisations.',
    visitNl: 'Rondleiding van circa een uur. Tijdens de huidige verbouwing zijn museum en educatief park niet toegankelijk; de grot wel controleren.',
    visitFr: 'Visite guidée d’environ une heure. Pendant les travaux, musée et parc pédagogique sont fermés ; vérifier la grotte.',
    whyWorthItNl: 'Voor geologie en puur ondergronds formaat; niet vermomd als prehistorische vindplaats.',
    whyWorthItFr: 'Pour la géologie et l’échelle souterraine, sans le présenter comme un site préhistorique.',
    source: 'Gouffre de Proumeyssac — officiële informatie',
    sourceUrl: 'https://www.gouffre-proumeyssac.com/'
  },
  {
    id: 'grotte-de-foissac',
    category: 'grot-ondergronds',
    name: 'Grotte préhistorique de Foissac',
    lon: 2.006713,
    lat: 44.5026281,
    driveMinutes: 91,
    driveKm: 83,
    markerType: 'ingang',
    descriptionNl: 'Actief grottenstelsel met ondergrondse rivier en een uitzonderlijk bewaard chalcolithisch graf- en bewoningscomplex.',
    descriptionFr: 'Réseau actif avec rivière souterraine et exceptionnel ensemble funéraire et domestique chalcolithique conservé sur place.',
    visitNl: 'Begeleide tocht van 1–1,5 uur; circa 12 °C, 78 treden en geen lift.',
    visitFr: 'Visite guidée de 1 h à 1 h 30 ; environ 12 °C, 78 marches et aucun ascenseur.',
    whyWorthItNl: 'Hier zie je niet alleen een grot, maar ook menselijke resten en voorwerpen in hun ondergrondse context.',
    whyWorthItFr: 'On n’y voit pas seulement une grotte, mais aussi des vestiges humains et objets dans leur contexte souterrain.',
    source: 'Grotte de Foissac — officiële informatie',
    sourceUrl: 'https://grotte-foissac.fr/'
  },
  {
    id: 'grotte-de-villars',
    category: 'grot-ondergronds',
    name: 'Grotte préhistorique de Villars',
    lon: 0.785301,
    lat: 45.4425621,
    driveMinutes: 152,
    driveKm: 130.8,
    markerType: 'ingang',
    descriptionNl: 'Groot natuurlijk grottenstelsel met originele prehistorische schilderingen, waaronder een zeldzame mens-bisonscène.',
    descriptionFr: 'Grand réseau naturel avec peintures préhistoriques originales, dont une rare scène associant un homme et un bison.',
    visitNl: 'Begeleid en seizoensgebonden; ongeveer 45 minuten ondergronds. Reserveer bij drukte.',
    visitFr: 'Visite guidée et saisonnière, environ 45 minutes sous terre. Réserver en période chargée.',
    whyWorthItNl: 'De ongebruikelijke combinatie van echte grotkunst en een rijk natuurlijk druipsteenlandschap.',
    whyWorthItFr: 'L’association inhabituelle d’un art pariétal authentique et d’un riche décor de concrétions.',
    source: 'Grotte de Villars — officiële informatie',
    sourceUrl: 'https://grotte-villars.com/'
  },
  {
    id: 'grotte-du-mas-d-azil',
    category: 'grot-ondergronds',
    name: 'Grotte du Mas-d’Azil',
    lon: 1.3555101,
    lat: 43.0693305,
    driveMinutes: 179,
    driveKm: 220.6,
    markerType: 'ingang',
    descriptionNl: 'Gigantische door een rivier uitgesleten tunnel met prehistorische bewoning, Azilien-typevondsten en latere schuilgeschiedenis.',
    descriptionFr: 'Immense tunnel creusé par la rivière, occupé à la Préhistoire, site éponyme de l’Azilien et refuge historique.',
    visitNl: 'Rondleiding en museum in het dorp; de afgesloten versierde galerij wordt als facsimile getoond. Reserveren aanbevolen.',
    visitFr: 'Visite guidée et musée au village ; la galerie ornée fermée est présentée en fac-similé. Réserver.',
    whyWorthItNl: 'Een complete dagtocht: geologisch spektakel en een typevindplaats die een archeologische periode haar naam gaf.',
    whyWorthItFr: 'Une vraie journée : spectacle géologique et site ayant donné son nom à une période archéologique.',
    source: 'Sites Touristiques Ariège — Mas-d’Azil',
    sourceUrl: 'https://www.sites-touristiques-ariege.fr/grotte-du-mas-dazil-2/'
  },
  {
    id: 'grotte-de-niaux',
    category: 'grot-ondergronds',
    name: 'Grotte de Niaux',
    lon: 1.5937269,
    lat: 42.8198511,
    driveMinutes: 185,
    driveKm: 251.6,
    markerType: 'ingang',
    descriptionNl: 'Authentieke beschilderde grot in de Pyreneeën, met de beroemde zwarte dierfiguren en tekens van de Salon Noir.',
    descriptionFr: 'Grotte ornée authentique des Pyrénées, célèbre pour les figures animales noires et signes du Salon Noir.',
    visitNl: 'Uitsluitend op reservering; circa 1 uur 45 lopen met lamp over natuurlijke, soms gladde bodem. Niet geschikt op een slechte kniedag.',
    visitFr: 'Sur réservation ; environ 1 h 45 à la lampe sur sol naturel parfois glissant. Parcours physiquement exigeant.',
    whyWorthItNl: 'Net boven drie uur, maar een van de zeldzame grote beschilderde grotten waar je de originelen nog kunt zien.',
    whyWorthItFr: 'Un peu plus de trois heures, mais l’une des rares grandes grottes ornées encore ouvertes devant les originaux.',
    source: 'Sites Touristiques Ariège — Grotte de Niaux',
    sourceUrl: 'https://www.sites-touristiques-ariege.fr/grotte-de-niaux/'
  },
  {
    id: 'grottes-de-gargas',
    category: 'grot-ondergronds',
    name: 'Grottes de Gargas · Nestploria',
    lon: 0.5379455,
    lat: 43.0623716,
    driveMinutes: 182,
    driveKm: 251.3,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Authentieke versierde grotten met gravures, tekens en een uitzonderlijke concentratie negatieve handafdrukken.',
    descriptionFr: 'Grottes ornées authentiques aux gravures, signes et à l’exceptionnelle concentration de mains négatives.',
    visitNl: 'Begeleid bezoek met beperkt aantal plaatsen; reserveren en minstens 30 minuten voor aanvang bij Nestploria melden.',
    visitFr: 'Visite guidée à jauge limitée ; réserver et se présenter à Nestploria 30 minutes avant.',
    whyWorthItNl: 'Net boven drie uur; de vele echte handstencils maken dit inhoudelijk uniek, niet alleen beroemd.',
    whyWorthItFr: 'Un peu plus de trois heures ; les nombreuses mains authentiques rendent le lieu réellement unique.',
    source: 'Grottes de Gargas · Nestploria — officiële informatie',
    sourceUrl: 'https://grottesdegargas.fr/'
  },
  {
    id: 'grotte-de-pair-non-pair',
    category: 'grot-ondergronds',
    name: 'Grotte de Pair-non-Pair',
    lon: -0.5016959,
    lat: 45.0393335,
    driveMinutes: 193,
    driveKm: 208,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Zeer vroege versierde grot met meer dan 30.000 jaar oude gravures van onder meer paarden, mammoet en reuzenhert.',
    descriptionFr: 'Très ancienne grotte ornée aux gravures de plus de 30 000 ans : chevaux, mammouth, mégacéros et autres animaux.',
    visitNl: 'Alleen begeleid, vanaf 7 jaar en reservering verplicht. De route is circa 3 uur 13 en dus een bewuste uitzondering.',
    visitFr: 'Uniquement en visite guidée, dès 7 ans, réservation obligatoire. Environ 3 h 13 : exception assumée.',
    whyWorthItNl: 'De oudste originele grotkunst in deze selectie rechtvaardigt dertien brutale minuten buiten de grens.',
    whyWorthItFr: 'L’art pariétal original le plus ancien de cette sélection justifie treize minutes d’indiscipline.',
    source: 'Centre des monuments nationaux — Pair-non-Pair',
    sourceUrl: 'https://www.pair-non-pair.fr/'
  },
  {
    id: 'uxellodunum-puy-issolud',
    category: 'prehistorie-archeologie',
    name: 'Uxellodunum · Puy d’Issolud',
    lon: 1.6646451,
    lat: 44.9576272,
    driveMinutes: 85,
    driveKm: 73.2,
    markerType: 'toegangsroute',
    descriptionNl: 'Archeologisch slagveld bij het Gallische oppidum dat officieel is geïdentificeerd als de laatste grote strijd tegen Caesar.',
    descriptionFr: 'Champ de bataille archéologique de l’oppidum gaulois officiellement identifié comme la dernière grande bataille contre César.',
    visitNl: 'Marker is het exacte publieke punt Pont Roux op de gepubliceerde toegangsroute naar Loulié; volg het ontmoetingspunt uit de reservering. Beperkt PMR.',
    visitFr: 'Repère au point public exact Pont Roux sur l’accès publié vers Loulié ; suivre le rendez-vous de la réservation. PMR limité.',
    whyWorthItNl: 'Voor Keltisch-Romeinse geschiedenis is dit geen mooi bordje, maar het echte belegeringslandschap.',
    whyWorthItFr: 'Pour l’histoire gauloise et romaine, ce n’est pas un simple panneau mais le paysage réel du siège.',
    source: 'Association des Amis d’Uxellodunum — officiële informatie',
    sourceUrl: 'https://www.uxellodunum.com/'
  },
  {
    id: 'vesunna-perigueux',
    category: 'prehistorie-archeologie',
    name: 'Vesunna · Périgueux',
    lon: 0.7132096,
    lat: 45.1794786,
    driveMinutes: 108,
    driveKm: 101.9,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Gallo-Romeinse domus met vloeren, muren en vondsten, ter plekke beschermd door het transparante museum van Jean Nouvel.',
    descriptionFr: 'Domus gallo-romaine avec sols, murs et objets, protégée in situ par le musée transparent conçu par Jean Nouvel.',
    visitNl: 'Goed ingericht binnenmuseum; buiten staan ook de Tour de Vésone en resten van de laat-Romeinse ommuring.',
    visitFr: 'Musée couvert bien aménagé ; à l’extérieur, Tour de Vésone et vestiges du rempart du Bas-Empire.',
    whyWorthItNl: 'Een echte Romeinse woonlaag die je droog, leesbaar en zonder bergbeklimmen kunt bekijken.',
    whyWorthItFr: 'Une vraie couche d’habitat romain, lisible au sec et sans expédition alpine.',
    source: 'Vesunna — officiële informatie van Périgueux',
    sourceUrl: 'https://www.perigueux-vesunna.fr/'
  },
  {
    id: 'villa-gallo-romaine-montcaret',
    category: 'prehistorie-archeologie',
    name: 'Villa gallo-romaine de Montcaret',
    lon: 0.0636468,
    lat: 44.8583865,
    driveMinutes: 134,
    driveKm: 129.2,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Grote Gallo-Romeinse villa met privébaden, ontvangstruimten en uitzonderlijk goed bewaarde mozaïekvloeren.',
    descriptionFr: 'Grande villa gallo-romaine avec thermes privés, salles de réception et remarquables pavements de mosaïque conservés.',
    visitNl: 'Betaald monument, dinsdag–zondag en seizoensuren; laatste toegang ruim voor sluiting. Actuele hitte-uren controleren.',
    visitFr: 'Monument payant, du mardi au dimanche selon saison ; dernier accès avant fermeture. Vérifier les horaires de chaleur.',
    whyWorthItNl: 'Voor Romeinse materiële cultuur zijn de baden en mozaïeken veel meer dan een museumvitrine.',
    whyWorthItFr: 'Pour la culture matérielle romaine, bains et mosaïques valent bien davantage qu’une simple vitrine.',
    source: 'Centre des monuments nationaux — Montcaret',
    sourceUrl: 'https://www.villa-montcaret.fr/'
  },
  {
    id: 'cassinomagus',
    category: 'prehistorie-archeologie',
    name: 'Cassinomagus · Chassenon',
    lon: 0.7700873,
    lat: 45.8486837,
    driveMinutes: 172,
    driveKm: 229,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Uitgestrekt Gallo-Romeins complex met thermen die tot de best bewaarde antieke badgebouwen van Europa behoren.',
    descriptionFr: 'Vaste complexe gallo-romain dont les thermes comptent parmi les édifices balnéaires antiques les mieux conservés d’Europe.',
    visitNl: 'Park van circa 15 hectare met vrij of begeleid bezoek; opening is seizoensgebonden. Reken op een halve dag ter plaatse.',
    visitFr: 'Parc d’environ 15 hectares, visite libre ou guidée ; ouverture saisonnière. Prévoir une demi-journée.',
    whyWorthItNl: 'Bijna drie uur rijden, maar hier staat nog genoeg Romeins muurwerk om de schaal werkelijk te voelen.',
    whyWorthItFr: 'Presque trois heures, mais assez de maçonnerie romaine subsiste pour ressentir réellement l’échelle du lieu.',
    source: 'Cassinomagus Parc archéologique — officiële informatie',
    sourceUrl: 'https://www.cassinomagus.fr/'
  },
  {
    id: 'maison-forte-de-reignac',
    category: 'kasteel-ruine',
    name: 'Maison Forte de Reignac',
    lon: 1.0552436,
    lat: 44.9795083,
    driveMinutes: 70,
    driveKm: 64,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Uitzonderlijk bewaard en ingericht kasteel dat letterlijk tegen en in een prehistorisch gebruikte rotswand is gebouwd.',
    descriptionFr: 'Château-falaise exceptionnellement conservé et meublé, construit contre et dans un escarpement occupé dès la Préhistoire.',
    visitNl: 'Vrij bezoek via een gemarkeerd parcours met meerdere niveaus en trappen; grotendeels beschut tegen regen.',
    visitFr: 'Visite libre sur parcours fléché, avec plusieurs niveaux et escaliers ; site largement abrité de la pluie.',
    whyWorthItNl: 'Prehistorische klifbewoning en een intact middeleeuws rotskasteel zitten hier in hetzelfde gebouw.',
    whyWorthItFr: 'Habitat préhistorique de falaise et château médiéval intact se superposent dans le même lieu.',
    source: 'Maison Forte de Reignac — officiële informatie',
    sourceUrl: 'https://www.maison-forte-reignac.com/'
  },
  {
    id: 'chateaux-de-bruniquel',
    category: 'kasteel-ruine',
    name: 'Châteaux de Bruniquel',
    lon: 1.6661113,
    lat: 44.0563126,
    driveMinutes: 84,
    driveKm: 92.3,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Twee kastelen op een 90 meter hoge rots boven Aveyron en Vère, met bouwlagen van middeleeuwen tot renaissance.',
    descriptionFr: 'Deux châteaux sur une falaise de 90 m dominant l’Aveyron et la Vère, du Moyen Âge à la Renaissance.',
    visitNl: 'Bezoekbaar monument in een steil dorp; kasseien, hoogteverschil en trappen. Opening en rondleidingen vooraf controleren.',
    visitFr: 'Monument visitable dans un village pentu ; pavés, dénivelé et marches. Vérifier horaires et visites.',
    whyWorthItNl: 'Sterk kasteelensemble én logisch startpunt voor de Gorges de l’Aveyron.',
    whyWorthItFr: 'Un ensemble castral majeur et un excellent départ pour les gorges de l’Aveyron.',
    source: 'Châteaux de Bruniquel — officiële informatie',
    sourceUrl: 'https://leschateauxdebruniquel.fr/'
  },
  {
    id: 'forteresse-de-penne',
    category: 'kasteel-ruine',
    name: 'Forteresse de Penne',
    lon: 1.7276663,
    lat: 44.0779685,
    driveMinutes: 94,
    driveKm: 103.1,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Spectaculaire middeleeuwse ruïne op een smalle rotspunt boven de Aveyron, met lopende restauratie en archeologie.',
    descriptionFr: 'Spectaculaire forteresse médiévale ruinée sur un piton dominant l’Aveyron, avec restauration et archéologie en cours.',
    visitNl: 'Vanaf het dorp stevig omhoog; ongelijke paden en veel hoogteverschil. Het interpretatiecentrum ligt in de vesting.',
    visitFr: 'Forte montée depuis le village, chemins irréguliers et dénivelé. Centre d’interprétation dans la forteresse.',
    whyWorthItNl: 'Dit is precies het soort ruïne dat landschap en verdedigingslogica in één blik uitlegt.',
    whyWorthItFr: 'La ruine qui explique d’un seul regard le paysage et la logique défensive.',
    source: 'Forteresse de Penne — officiële informatie',
    sourceUrl: 'https://www.chateau-penne.com/'
  },
  {
    id: 'forteresse-royale-de-najac',
    category: 'kasteel-ruine',
    name: 'Forteresse royale de Najac',
    lon: 1.9733276,
    lat: 44.2185305,
    driveMinutes: 101,
    driveKm: 93.4,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Koninklijke 13e-eeuwse vesting op een messcherpe bergrug boven een meander van de Aveyron.',
    descriptionFr: 'Forteresse royale du XIIIe siècle dressée sur une arête rocheuse au-dessus d’un méandre de l’Aveyron.',
    visitNl: 'Alleen te voet via een smal pad met fors verval; daarna volgen trappen in de vesting. Niet de zachte-knieoptie.',
    visitFr: 'Accès uniquement à pied par un chemin étroit à fort dénivelé, puis escaliers. Parcours exigeant.',
    whyWorthItNl: 'Najac koppelt een uitzonderlijke burcht aan precies het beek-, rug- en rivierlandschap dat jou interesseert.',
    whyWorthItFr: 'Najac unit une forteresse majeure au paysage de crête, vallon et rivière qui donne tout son sens au site.',
    source: 'Tourisme Aveyron — Forteresse royale de Najac',
    sourceUrl: 'https://www.tourisme-aveyron.com/fr/diffusio/patrimoine-culturel-visites/forteresse-royale-de-najac-najac_TFO18778650928'
  },
  {
    id: 'cite-de-carcassonne',
    category: 'kasteel-ruine',
    name: 'Cité de Carcassonne · château et remparts',
    lon: 2.3640453,
    lat: 43.2063289,
    driveMinutes: 171,
    driveKm: 238.3,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Enorme versterkte stad met dubbele ommuring, tientallen torens, een grafelijk kasteel en middeleeuwse bouwfasen.',
    descriptionFr: 'Immense cité fortifiée à double enceinte, dizaines de tours, château comtal et multiples phases médiévales.',
    visitNl: 'De stad is vrij toegankelijk; kasteel en hoge remparts zijn betaald. Trek circa twee uur uit en verwacht trappen en kasseien.',
    visitFr: 'Cité en accès libre ; château et remparts hauts payants. Compter environ deux heures, marches et pavés.',
    whyWorthItNl: 'Een grote naam die de rit wél verdient: de schaal van een compleet verdedigingssysteem is nergens subtiel.',
    whyWorthItFr: 'Un grand nom qui mérite la route : l’échelle d’un système défensif complet n’a rien de subtil.',
    source: 'Centre des monuments nationaux — Cité de Carcassonne',
    sourceUrl: 'https://www.remparts-carcassonne.fr/'
  },
  {
    id: 'village-martyr-oradour',
    category: 'historisch-dorp',
    name: 'Village martyr d’Oradour-sur-Glane',
    lon: 1.0400627,
    lat: 45.9293405,
    driveMinutes: 155,
    driveKm: 204.4,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Het verwoeste dorp dat na het bloedbad van 10 juni 1944 als ruïnelandschap en nationaal memoriaal is bewaard.',
    descriptionFr: 'Le village détruit lors du massacre du 10 juin 1944, conservé en ruines comme mémorial national.',
    visitNl: 'Sober herdenkingsbezoek. Het museumgebouw is tot juni 2027 dicht, maar het dorp blijft via een tijdelijke ontvangst toegankelijk.',
    visitFr: 'Visite de mémoire. Le musée est fermé jusqu’en juin 2027, mais le village reste accessible par l’accueil temporaire.',
    whyWorthItNl: 'Geen gezellig verlaten dorp, wel een uniek intact landschap van geheugen. Alleen bezoeken als je daar ruimte voor hebt.',
    whyWorthItFr: 'Pas un village abandonné pittoresque, mais un paysage de mémoire intact. Visite émotionnellement lourde.',
    source: 'Centre de la mémoire d’Oradour — officiële informatie',
    sourceUrl: 'https://www.visitlimousin.com/centre-de-la-memoire-doradour-sur-glane/'
  },
  {
    id: 'tours-de-merle',
    category: 'kasteel-ruine',
    name: 'Tours de Merle',
    lon: 2.0746566,
    lat: 45.0642823,
    driveMinutes: 117,
    driveKm: 106,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Ruïnes van een compleet middeleeuws castrum met meerdere adellijke torens in een beboste kloof boven de Maronne.',
    descriptionFr: 'Ruines d’un castrum médiéval complet aux multiples tours seigneuriales, dans une gorge boisée au-dessus de la Maronne.',
    visitNl: 'Steile en onregelmatige paden; voor beperkte mobiliteit zeer moeilijk tot onmogelijk. Goede schoenen zijn geen decoratie.',
    visitFr: 'Chemins raides et irréguliers ; très difficile, voire impossible, à mobilité réduite. Bonnes chaussures indispensables.',
    whyWorthItNl: 'Geen los kasteel, maar een verdwenen machtsnederzetting midden in oud rivier- en boslandschap.',
    whyWorthItFr: 'Pas un château isolé, mais une ancienne cité seigneuriale dans son paysage de forêt et de rivière.',
    source: 'Tours de Merle — officiële informatie',
    sourceUrl: 'https://toursdemerle.fr/'
  },
  {
    id: 'chateau-castelnau-bretenoux',
    category: 'kasteel-ruine',
    name: 'Château de Castelnau-Bretenoux',
    lon: 1.8256582,
    lat: 44.8990897,
    driveMinutes: 80,
    driveKm: 65.1,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Imposante roodstenen vesting van het Quercy, gegroeid rond een 13e-eeuwse donjon en hoog boven de Dordognevallei.',
    descriptionFr: 'Imposante forteresse de pierre rouge du Quercy, développée autour d’un donjon du XIIIe siècle au-dessus de la Dordogne.',
    visitNl: 'Betaald staatsmonument; hoogteverschil, binnenplaatsen en trappen. Actuele bezoekvorm vooraf controleren.',
    visitFr: 'Monument national payant ; dénivelé, cours et escaliers. Vérifier les modalités de visite.',
    whyWorthItNl: 'Een van de sterkste echte vestingen van het Quercy, nauwelijks verder dan Padirac.',
    whyWorthItFr: 'L’une des forteresses les plus puissantes du Quercy, à peine plus loin que Padirac.',
    source: 'Centre des monuments nationaux — Castelnau-Bretenoux',
    sourceUrl: 'https://www.castelnau-bretenoux.fr/'
  },
  {
    id: 'cascade-cirque-autoire',
    category: 'water-landschap',
    name: 'Cirque et cascade d’Autoire',
    lon: 1.8105199,
    lat: 44.8444716,
    driveMinutes: 67,
    driveKm: 58.4,
    markerType: 'object',
    descriptionNl: 'Hoge waterval in een kalkstenen keteldal, omringd door steile wanden, bos en resten van het Château des Anglais.',
    descriptionFr: 'Haute cascade dans un cirque calcaire, entouré de falaises, de bois et des vestiges du Château des Anglais.',
    visitNl: 'Alleen te voet; de korte lus is circa 2,7 km met 114 meter stijging. Gladde bodem mogelijk, grote ronde is steiler.',
    visitFr: 'Accès à pied ; boucle courte d’environ 2,7 km et 114 m de dénivelé. Sol glissant possible, grand tour plus raide.',
    whyWorthItNl: 'Een half uur verder was niet eens nodig: deze had simpelweg al in de eerste selectie moeten staan.',
    whyWorthItFr: 'Même pas besoin d’une demi-heure de plus : ce lieu aurait dû figurer dans la première sélection.',
    source: 'Lot Tourisme — Cirque et cascade d’Autoire',
    sourceUrl: 'https://www.tourisme-lot.com/vallees-intimes/cirque-et-cascade-d-autoire/'
  },
  {
    id: 'foret-de-gresigne',
    category: 'natuur-bos',
    name: 'Forêt domaniale de Grésigne',
    lon: 1.7659226,
    lat: 44.0204555,
    driveMinutes: 96,
    driveKm: 105.3,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Groot aaneengesloten eikenbos met oude bomen, Romeinse resten, houtskool- en glasovenplaatsen en uitzonderlijk veel insectensoorten.',
    descriptionFr: 'Vaste chênaie continue aux vieux arbres, vestiges gallo-romains, charbonnières, fours de verriers et riche entomofaune.',
    visitNl: 'Marker bij de Grande Baraque, een officieel startpunt voor wandelingen. Geen oerbos maar oud, beheerd bos; paden zijn niet PMR.',
    visitFr: 'Repère à la Grande Baraque, départ officiel de balades. Forêt ancienne mais gérée, pas primaire ; sentiers non PMR.',
    whyWorthItNl: 'Dit benadert jouw “oerbos”-wens eerlijk: oud en soortenrijk, maar zonder sprookjes over onaangeraakte wildernis.',
    whyWorthItFr: 'Une réponse honnête à la recherche de vieille forêt : ancienne et riche, sans mythe de nature vierge.',
    source: 'Office national des forêts — Forêt de Grésigne',
    sourceUrl: 'https://www.onf.fr/vivre-la-foret/que-faire-en-foret/balade-activites-en-foret/%2B/c0e%3A%3Aforet-de-gresigne-ou-lexcursion-dans-la-plus-grande-chenaie-du-sud-de-la-france.html'
  },
  {
    id: 'canyon-de-bozouls',
    category: 'water-landschap',
    name: 'Canyon de Bozouls',
    lon: 2.7196172,
    lat: 44.4700989,
    driveMinutes: 152,
    driveKm: 156.4,
    markerType: 'uitzichtpunt',
    descriptionNl: 'Hoefijzervormige canyon van circa 400 meter breed en 100 meter diep, uitgesneden door de Dourdou rond de oude dorpskern.',
    descriptionFr: 'Canyon en fer à cheval d’environ 400 m de diamètre et 100 m de profondeur, creusé par le Dourdou autour du vieux bourg.',
    visitNl: 'Direct uitzicht vanaf het dorp; de volledige aangelegde route is circa 6 km met afdaling en hangbruggen.',
    visitFr: 'Vue immédiate depuis le bourg ; circuit aménagé complet d’environ 6 km avec descente et passerelles.',
    whyWorthItNl: 'Geologie, watererosie en een oude nederzettingsrand zijn hier in één overzicht te lezen.',
    whyWorthItFr: 'Géologie, érosion fluviale et implantation ancienne se lisent ici dans un seul panorama.',
    source: 'Département de l’Aveyron — Canyon de Bozouls',
    sourceUrl: 'https://espacenature.aveyron.fr/ens/canyon-de-bozouls'
  },
  {
    id: 'puy-mary-pas-de-peyrol',
    category: 'uitzicht-hoogte',
    name: 'Puy Mary · Pas de Peyrol',
    lon: 2.6701311,
    lat: 45.1177443,
    driveMinutes: 176,
    driveKm: 164.2,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Spectaculaire piramidevormige top van 1.787 meter in het oude vulkaanmassief van de Cantal, met stervormige gletsjerdalen.',
    descriptionFr: 'Sommet pyramidal spectaculaire de 1 787 m dans l’ancien massif volcanique du Cantal, entouré de vallées glaciaires en étoile.',
    visitNl: 'Marker bij de hoge wegpas. De top vraagt circa 1,5 uur retour over een steil trappenpad; de pas is meestal alleen mei–oktober open.',
    visitFr: 'Repère au col routier. Sommet en environ 1 h 30 aller-retour par un sentier raide à marches ; col ouvert surtout de mai à octobre.',
    whyWorthItNl: 'Dit is de echte bergdag binnen drie uur; het uitzicht vanaf de pas blijft ook zonder topklim de rit waard.',
    whyWorthItFr: 'La vraie journée de montagne à moins de trois heures ; la vue du col vaut déjà le trajet sans gravir le sommet.',
    source: 'Grand Site de France Puy Mary — officiële informatie',
    sourceUrl: 'https://www.puymary.fr/'
  },
  {
    id: 'grottes-de-presque',
    category: 'grot-ondergronds',
    name: 'Grottes de Presque',
    lon: 1.84043,
    lat: 44.85129,
    driveMinutes: 69,
    driveKm: 61.1,
    markerType: 'ingang',
    descriptionNl: 'Compacte druipsteengrot met uitzonderlijk slanke concreties en stalagmieten van acht tot tien meter hoog.',
    descriptionFr: 'Grotte à concrétions compacte, remarquable par ses formes élancées et ses stalagmites hautes de huit à dix mètres.',
    visitNl: 'Officiële rondleiding van circa 40–45 minuten; het parcours telt slechts veertien traptreden. Opening en vertrektijden vooraf controleren.',
    visitFr: 'Visite guidée officielle d’environ 40 à 45 minutes ; le parcours ne compte que quatorze marches. Vérifier horaires et ouverture.',
    whyWorthItNl: 'Een toegankelijke echte grot voor wie veel speleologische sfeer wil zonder zware ondergrondse route.',
    whyWorthItFr: 'Une vraie ambiance souterraine très accessible, sans parcours physique exigeant.',
    source: 'Grottes de Presque — officiële bezoekerssite',
    sourceUrl: 'https://grottes-de-presque.com/'
  },
  {
    id: 'chateau-cenevieres',
    category: 'kasteel-ruine',
    name: 'Château de Cénevières',
    lon: 1.7506171,
    lat: 44.4627138,
    driveMinutes: 70,
    driveKm: 63.5,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Middeleeuws en renaissancistisch kasteel dat spectaculair op de rotswand boven een meander van de Lot staat.',
    descriptionFr: 'Château médiéval et Renaissance posé de façon spectaculaire sur la falaise au-dessus d’un méandre du Lot.',
    visitNl: 'Te bezoeken volgens het seizoensprogramma, geleid of zelfstandig afhankelijk van de dag. Opening vooraf controleren.',
    visitFr: 'Visites selon le programme saisonnier, guidées ou libres suivant les jours. Vérifier l’ouverture avant le départ.',
    whyWorthItNl: 'De combinatie van architectuur, klifligging en uitzicht over de Lot maakt dit meer dan alleen een kasteelbezoek.',
    whyWorthItFr: 'Architecture, implantation sur la falaise et vue sur le Lot en font bien plus qu’une simple visite de château.',
    source: 'Château de Cénevières — officiële bezoekerssite',
    sourceUrl: 'https://chateau-cenevieres.com/'
  },
  {
    id: 'vieux-sauliac',
    category: 'historisch-dorp',
    name: 'Vieux Sauliac · verlaten klifdorp',
    lon: 1.71801643,
    lat: 44.52050766,
    driveMinutes: 89,
    driveKm: 49.6,
    markerType: 'object',
    descriptionNl: 'Resten van een oud, deels semi-troglodytisch dorp tegen de Célé-klif, met kasteelsporen; de laatste inwoner vertrok rond 1950.',
    descriptionFr: 'Vestiges d’un ancien village en partie semi-troglodytique adossé à la falaise du Célé, avec traces castrales ; le dernier habitant partit vers 1950.',
    visitNl: 'Bereikbaar via de officiële lus Le chemin de Pierre-Levée. Blijf op het gemarkeerde pad: delen zijn privé en het landschap valt onder Natura 2000.',
    visitFr: 'Accessible par la boucle officielle Le chemin de Pierre-Levée. Rester sur le balisage : certaines parties sont privées et le secteur est Natura 2000.',
    whyWorthItNl: 'Prehistorische context, dolmen, verlaten bewoning en kliflandschap liggen hier op één korte officiële wandeling.',
    whyWorthItFr: 'Contexte préhistorique, dolmen, habitat abandonné et falaise se réunissent sur une courte randonnée officielle.',
    source: 'Lot Tourisme — Le chemin de Pierre-Levée',
    sourceUrl: 'https://www.tourisme-lot.com/offres/le-chemin-de-pierre-levee-sauliac-sur-cele-fr-967867/'
  },
  {
    id: 'marais-bonnefont',
    category: 'natuur-bos',
    name: 'Marais de Bonnefont',
    lon: 1.79909725,
    lat: 44.8200993,
    driveMinutes: 65,
    driveKm: 54.6,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Regionaal natuurreservaat van 42 hectare met het grootste rietveld van de Lot en een uitzonderlijke inventaris van planten, dieren en schimmels.',
    descriptionFr: 'Réserve naturelle régionale de 42 hectares abritant la plus grande roselière du Lot et une remarquable diversité de plantes, d’animaux et de champignons.',
    visitNl: 'Vrij te beleven via een route van ongeveer twee kilometer met vlonders. Blijf strikt op de aangelegde paden en controleer seizoensinformatie.',
    visitFr: 'Découverte libre par un parcours d’environ deux kilomètres avec caillebotis. Rester strictement sur les cheminements et vérifier les informations saisonnières.',
    whyWorthItNl: 'Een klein maar soortenrijk moeraslandschap dat sterk afwijkt van de droge kalkcausses rondom.',
    whyWorthItFr: 'Un petit marais très riche en espèces, en contraste saisissant avec les causses calcaires secs voisins.',
    source: 'Réserves naturelles de France — Marais de Bonnefont',
    sourceUrl: 'https://reserves-naturelles.org/reserves/marais-de-bonnefont/'
  },
  {
    id: 'igues-aujols',
    category: 'grot-ondergronds',
    name: 'Igues d’Aujols',
    lon: 1.52678158,
    lat: 44.4431121,
    driveMinutes: 46,
    driveKm: 34.9,
    markerType: 'uitzichtpunt',
    descriptionNl: 'Drie grote karstputten; vanaf het beveiligde uitzichtpunt kijk je in de Igue de la Ramade, de grootste van het complex.',
    descriptionFr: 'Trois grands puits karstiques ; le belvédère sécurisé domine l’Igue de la Ramade, la plus vaste du complexe.',
    visitNl: 'Alleen bekijken vanaf het officiële, beveiligde punt. Blijf achter de afscheiding; de diepe holtes en beschermde vogels maken afdalen onverantwoord.',
    visitFr: 'Observer uniquement depuis le point officiel sécurisé. Rester derrière la protection ; profondeur et oiseaux protégés interdisent toute descente improvisée.',
    whyWorthItNl: 'Een rauwe karstvorm die aan het oppervlak direct laat zien hoe hol en ingestort het kalkplateau kan zijn.',
    whyWorthItFr: 'Une forme karstique brute qui montre immédiatement à quel point le plateau calcaire peut être creux et effondré.',
    source: 'Parc naturel régional des Causses du Quercy — Igues d’Aujols',
    sourceUrl: 'https://www.parc-causses-du-quercy.fr/fiche-tourisme/igues-daujols/'
  },
  {
    id: 'cirque-venes',
    category: 'uitzicht-hoogte',
    name: 'Cirque de Vènes',
    lon: 1.68070497,
    lat: 44.4633856,
    driveMinutes: 66,
    driveKm: 57.7,
    markerType: 'uitzichtpunt',
    descriptionNl: 'Grote kalkstenen kom boven de Lotvallei, met weids zicht op de kliffen van Tour-de-Faure en richting Calvignac.',
    descriptionFr: 'Grand cirque calcaire dominant la vallée du Lot, avec une vue ample sur les falaises de Tour-de-Faure et vers Calvignac.',
    visitNl: 'Openbaar uitzichtpunt; blijf op het pad en uit de buurt van de klifrand, zeker bij wind of nat weer.',
    visitFr: 'Belvédère public ; rester sur le sentier et loin du bord de falaise, surtout par vent ou terrain humide.',
    whyWorthItNl: 'Een van de helderste plekken om de schaal van het ingesneden Lotlandschap in één blik te begrijpen.',
    whyWorthItFr: 'L’un des meilleurs endroits pour saisir d’un seul regard l’échelle du paysage encaissé du Lot.',
    source: 'Parc naturel régional des Causses du Quercy — Cirque de Vènes',
    sourceUrl: 'https://www.parc-causses-du-quercy.fr/fiche-tourisme/le-cirque-de-venes/'
  },
  {
    id: 'bancourel',
    category: 'uitzicht-hoogte',
    name: 'Pigeonnier en uitzichtpunt du Bancourel',
    lon: 1.66580261,
    lat: 44.4663564,
    driveMinutes: 66,
    driveKm: 50.4,
    markerType: 'uitzichtpunt',
    descriptionNl: 'Historische duiventoren met een hoog uitzicht over de Lotvallei en het op de rots gebouwde Saint-Cirq-Lapopie.',
    descriptionFr: 'Pigeonnier historique accompagné d’un panorama élevé sur la vallée du Lot et le village perché de Saint-Cirq-Lapopie.',
    visitNl: 'Publiek uitzichtpunt; benader via de aangegeven paden en respecteer omliggende percelen.',
    visitFr: 'Belvédère public ; l’aborder par les itinéraires indiqués et respecter les parcelles voisines.',
    whyWorthItNl: 'Een rustig alternatief voor het drukke dorpscentrum, met juist het klassieke totaalbeeld op Saint-Cirq.',
    whyWorthItFr: 'Une alternative calme au centre très fréquenté, offrant précisément la vue d’ensemble classique sur Saint-Cirq.',
    source: 'Parc naturel régional des Causses du Quercy — Bancourel',
    sourceUrl: 'https://www.parc-causses-du-quercy.fr/fiche-tourisme/pigeonnier-et-point-de-vue-du-bancourel/'
  },
  {
    id: 'phosphatiere-tempories-sight',
    category: 'grot-ondergronds',
    name: 'Phosphatière des Tempories',
    lon: 1.6500091552734,
    lat: 44.360687774813,
    driveMinutes: 61,
    driveKm: 54.8,
    markerType: 'bezoekerspunt',
    descriptionNl: 'Historische fosfaatmijn in een karstholte, ingericht met observatieplatforms en een gerestaureerde mijnwerkershut.',
    descriptionFr: 'Ancienne mine de phosphate aménagée dans une cavité karstique, avec plateformes d’observation et cabane de mineurs restaurée.',
    visitNl: 'Gebruik uitsluitend het ingerichte parcours. De site ligt in het geologische natuurreservaat: niet in schachten afdalen, niet hakken en niets meenemen.',
    visitFr: 'Utiliser exclusivement le parcours aménagé. Le site appartient à la réserve géologique : ne pas descendre dans les puits, frapper la roche ni prélever.',
    whyWorthItNl: 'Een van de zeldzame plekken waar mijnbouwgeschiedenis, karst en fossielhoudende fosfaatvulling veilig samen te zien zijn.',
    whyWorthItFr: 'L’un des rares lieux où histoire minière, karst et remplissage phosphaté fossilifère se lisent ensemble en sécurité.',
    source: 'Parc naturel régional des Causses du Quercy — Phosphatière des Tempories',
    sourceUrl: 'https://www.parc-causses-du-quercy.fr/points-interets/phosphatiere-des-tempories/'
  }
]

export const THEDIRAC_SIGHTS_LAYER_NAME = 'Bezienswaardigheden'
